// features/workstations/components/add-widgets-overlay.js
// ═══════════════════════════════════════════════════════════
// ADD WIDGETS OVERLAY — transient library shown only when user
// clicks "+ Add Widgets" in the edit-mode topbar.
// ═══════════════════════════════════════════════════════════
// Multi-select with checkboxes. User picks N widgets, clicks
// "Add (N)", overlay closes, widgets are appended to the
// dashboard. Cancel discards selections.
//
// Drag/resize/remove of EXISTING widgets happens inline on the
// full-width dashboard — this overlay is not involved in those.
// Coming-Soon widgets render disabled (cannot be selected).
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { TIER, CATEGORY_LABELS } from '../../../lib/widgets/registry';

export default function AddWidgetsOverlay({
  open,
  onClose,
  library,
  onAddMultiple,
}) {
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Reset selection whenever the overlay opens fresh
  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setError(null);
    }
  }, [open]);

  // Esc closes the overlay
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  if (!library) {
    return (
      <aside className="ws-addov" role="dialog" aria-modal="true" aria-labelledby="ws-addov-title">
        <div className="ws-addov-loading">Loading widgets…</div>
      </aside>
    );
  }

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      await onAddMultiple?.(Array.from(selected));
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Add failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside
      className="ws-addov"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ws-addov-title"
    >
      <header className="ws-addov-header">
        <div>
          <div id="ws-addov-title" className="ws-addov-title">Add Widgets</div>
          <div className="ws-addov-subtitle">
            Select one or more widgets to add to your dashboard.
          </div>
        </div>
        <button
          type="button"
          className="ws-addov-close"
          onClick={onClose}
          aria-label="Close add widgets"
          title="Close (Esc)"
        >
          ✕
        </button>
      </header>

      <div className="ws-addov-body">
        {/* Available widgets — built + suitable + not currently active */}
        <Section
          title="Available"
          eyebrow="Built and suitable for this workstation"
          count={library.available.length}
        >
          {library.available.length === 0 ? (
            <Empty text="Everything available is already on your dashboard." />
          ) : (
            library.available.map((w) => (
              <WidgetRow
                key={w.key}
                widget={w}
                checked={selected.has(w.key)}
                onToggle={() => toggle(w.key)}
              />
            ))
          )}
        </Section>

        {/* Coming Soon — visible but disabled */}
        {library.coming_soon.length > 0 && (
          <Section
            title="Coming Soon"
            eyebrow="Planned but not built yet"
            count={library.coming_soon.length}
          >
            {library.coming_soon.map((w) => (
              <WidgetRow key={w.key} widget={w} disabled />
            ))}
          </Section>
        )}

        {/* Other — built but not for this workstation. User can still add. */}
        {library.other.length > 0 && (
          <Section
            title="Other"
            eyebrow="Built, not typically for this workstation"
            count={library.other.length}
          >
            {library.other.map((w) => (
              <WidgetRow
                key={w.key}
                widget={w}
                checked={selected.has(w.key)}
                onToggle={() => toggle(w.key)}
              />
            ))}
          </Section>
        )}

        <div className="ws-addov-footnote">
          Don&rsquo;t see what you need?{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); alert('Request widget — coming in a future batch.'); }}>
            Request a widget
          </a>{' '}
          and Shift will flag it for the team.
        </div>
      </div>

      <footer className="ws-addov-actions">
        {error && <div className="ws-addov-error">{error}</div>}
        <button
          type="button"
          className="ws-addov-cancel"
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ws-addov-confirm"
          onClick={handleAdd}
          disabled={busy || selected.size === 0}
        >
          {busy ? 'Adding…' : `+ Add ${selected.size || ''}`.trim()}
        </button>
      </footer>
    </aside>
  );
}

// ─── Section wrapper ────────────────────────────────────
function Section({ title, count, eyebrow, children }) {
  return (
    <section className="ws-addov-section">
      <header className="ws-addov-section-header">
        <div className="ws-addov-section-title">
          {title}
          {typeof count === 'number' && (
            <span className="ws-addov-section-count"> ({count})</span>
          )}
        </div>
        {eyebrow && <div className="ws-addov-section-eyebrow">{eyebrow}</div>}
      </header>
      <div className="ws-addov-section-body">{children}</div>
    </section>
  );
}

// ─── Widget row with checkbox ───────────────────────────
function WidgetRow({ widget, checked = false, disabled = false, onToggle }) {
  return (
    <label
      className={`ws-addov-row ${disabled ? 'is-disabled' : ''} ${checked ? 'is-checked' : ''}`}
    >
      <input
        type="checkbox"
        className="ws-addov-row-check"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        aria-label={`Add ${widget.name}`}
      />
      <div className="ws-addov-row-main">
        <div className="ws-addov-row-head">
          <span className="ws-addov-row-name">{widget.name}</span>
          {widget.tier && (
            <span
              className={`ws-addov-row-tier ws-addov-row-tier-${widget.tier}`}
              title={TIER[widget.tier] || `Tier ${widget.tier}`}
            >
              Tier {widget.tier}
            </span>
          )}
        </div>
        {widget.description && (
          <div className="ws-addov-row-desc">{widget.description}</div>
        )}
        <div className="ws-addov-row-meta">
          {widget.category && (
            <span className="ws-addov-row-pill">{CATEGORY_LABELS[widget.category] || widget.category}</span>
          )}
          {widget.coming_in && (
            <span className="ws-addov-row-pill ws-addov-row-pill-coming">{widget.coming_in}</span>
          )}
        </div>
      </div>
    </label>
  );
}

function Empty({ text }) {
  return <div className="ws-addov-empty">{text}</div>;
}
